import { Card, CardColors, CardTitle } from "$components/card";
import { BinIcon } from "$components/icons";
import type { RadioFieldProps } from "$components/inputs";
import RHFTextField from "$components/rhf/text-field";
import { RadioGroupField as RHFRadioField } from "$components/rhf-fields";
import type { Amp } from "$global-providers/rxdb-provider/collections/amp";
import { RXDB_AMP_COLLECTION_NAME } from "$global-providers/rxdb-provider/collections/amp";
import { DoseUnit } from "$global-providers/rxdb-provider/collections/prescription";
import type { Vmp } from "$global-providers/rxdb-provider/collections/vmp";
import { RXDB_VMP_COLLECTION_NAME } from "$global-providers/rxdb-provider/collections/vmp";
import type { YupSchemaToFieldNames } from "$routes/patient/_lib/types";
import { DrugGuidanceDeviation } from "$routes/patient/[:patientId]/_lib/clinical-decision-support-service/medication-guidelines-checker/guidelines-checker";
import { useClinicalDecisionSupportService } from "$routes/patient/[:patientId]/_lib/clinical-decision-support-service/provider";
import {
  AntibioticPrescriptionNudgeInfoBox,
  MalarialNudgeMessages,
  MalarialPrescriptionNudgeWarningBox,
} from "$routes/patient/[:patientId]/_lib/nudge-info-box";
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  styled,
  Typography,
} from "@material-ui/core";
import {
  AdministrationApproach,
  ClinicalProductType,
  RateUnit,
  TimeUnit,
} from "elephant-web/gql";
import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useRxData } from "rxdb-hooks";
import * as Yup from "yup";
import { AdministrationDetailInputs } from "./administration-detail-inputs";
import { calculateDispensableQuantity } from "./calculate-amount-to-dispense";
import {
  ClinicalProductAutocomplete,
  ClinicalProductAutocompleteMode,
} from "./clinical-product-autocomplete";
import { clinicalProductFormToDoseUnitMap } from "./map-clinical-product-form-to-dose-unit";

const JAVA_MAX_SAFE_INTEGER_VALUE = 2147483646;
const MAX_INT = Yup.number()
  .integer()
  .positive()
  .max(JAVA_MAX_SAFE_INTEGER_VALUE, "Entered value is too high");

const MAX_FLOAT = Yup.number()
  .positive()
  .max(JAVA_MAX_SAFE_INTEGER_VALUE, "Entered value is too high");

const NudgeBoxContainer = styled(Box)(({ theme }) => ({
  marginInline: 2,
  marginTop: theme.spacing(2),
}));

export const VALIDATION_SCHEMA = Yup.object({
  clinicalProductId: Yup.string(),
  clinicalProductType: Yup.mixed<ClinicalProductType>()
    .oneOf([...Object.values(ClinicalProductType), null])
    .nullable(),
  freeTextProduct: Yup.string(),
  howItsTaken: Yup.mixed<AdministrationApproach>()
    .oneOf(Object.values(AdministrationApproach))
    .required(),
  doseValue: MAX_FLOAT.min(0.01, "Must be over 0").required(),
  doseUnit: Yup.mixed<DoseUnit>().required(),
  rateValue: MAX_INT.min(1, "Must be over 0").when("howItsTaken", {
    is: AdministrationApproach.Outpatient || AdministrationApproach.AsRequired,
    then: (s) => s.required(),
    otherwise: (s) => s.nullable(),
  }),
  rateUnit: Yup.mixed<RateUnit>().when("howItsTaken", {
    is: AdministrationApproach.Outpatient || AdministrationApproach.AsRequired,
    then: (s) => s.required(),
    otherwise: (s) => s.nullable(),
  }),
  howLongForValue: MAX_INT.min(1, "Must be over 0").when("howItsTaken", {
    is: AdministrationApproach.Outpatient || AdministrationApproach.AsRequired,
    then: (s) => s.required(),
    otherwise: (s) => s.nullable(),
  }),
  howLongForUnit: Yup.mixed<TimeUnit>().when("howItsTaken", {
    is: AdministrationApproach.Outpatient || AdministrationApproach.AsRequired,
    then: (s) => s.required(),
    otherwise: (s) => s.nullable(),
  }),
  note: Yup.string().nullable(),
});

export const createDefaultPrescription = (): Yup.InferType<
  typeof VALIDATION_SCHEMA
> => ({
  clinicalProductId: "",
  clinicalProductType: null,
  freeTextProduct: "",
  howItsTaken: null,
  doseValue: null,
  doseUnit: DoseUnit.Tablet,
  rateValue: null,
  rateUnit: RateUnit.Day,
  howLongForValue: null,
  howLongForUnit: TimeUnit.Day,
  note: null,
});

type AddPrescriptionCardFormValues = Yup.InferType<typeof VALIDATION_SCHEMA>;
export type AddPrescriptionCardFieldNames = YupSchemaToFieldNames<
  typeof VALIDATION_SCHEMA
>;

const ADMINISTRATION_APPROACH_OPTIONS: RadioFieldProps["options"] = [
  {
    label: "Routinely",
    value: AdministrationApproach.Outpatient,
  },
  {
    label: "Once/stat",
    value: AdministrationApproach.Stat,
  },
  {
    label: "When required",
    value: AdministrationApproach.AsRequired,
  },
];

export const AddPrescriptionCard: FC<{
  patientWeight: string;
  patientAllergies: string[];
  fieldNames: AddPrescriptionCardFieldNames;
  onAddDiagnosis?: () => void;
  onDelete: () => void;
  trackRecommendationShown?: (
    recommendationVariant: DrugGuidanceDeviation,
  ) => void;
}> = ({
  patientWeight,
  patientAllergies,
  fieldNames,
  onAddDiagnosis,
  onDelete,
  trackRecommendationShown,
}) => {
  const { formatMessage } = useIntl();
  const { watch, setValue } = useFormContext<AddPrescriptionCardFormValues>();
  const [
    clinicalProductId,
    clinicalProductType,
    freeTextProduct,
    howItsTaken,
    doseValue,
    doseUnit,
    rateValue,
    rateUnit,
    howLongForValue,
    howLongForUnit,
  ] = watch([
    fieldNames.clinicalProductId as keyof AddPrescriptionCardFormValues,
    fieldNames.clinicalProductType as keyof AddPrescriptionCardFormValues,
    fieldNames.freeTextProduct as keyof AddPrescriptionCardFormValues,
    fieldNames.howItsTaken as keyof AddPrescriptionCardFormValues,
    fieldNames.doseValue as keyof AddPrescriptionCardFormValues,
    fieldNames.doseUnit as keyof AddPrescriptionCardFormValues,
    fieldNames.rateValue as keyof AddPrescriptionCardFormValues,
    fieldNames.rateUnit as keyof AddPrescriptionCardFormValues,
    fieldNames.howLongForValue as keyof AddPrescriptionCardFormValues,
    fieldNames.howLongForUnit as keyof AddPrescriptionCardFormValues,
  ]);

  const { checkMedicationGuidanceDeviation } =
    useClinicalDecisionSupportService();

  const clinicalProductChangeSideEffects = ({ formId }: { formId: string }) => {
    const associatedDoseUnit = clinicalProductFormToDoseUnitMap[formId];
    setValue(fieldNames.doseValue as keyof AddPrescriptionCardFormValues, null);
    setValue(
      fieldNames.doseUnit as keyof AddPrescriptionCardFormValues,
      associatedDoseUnit ?? DoseUnit.Tablet,
    );
    setValue(fieldNames.rateValue as keyof AddPrescriptionCardFormValues, null);
    setValue(
      fieldNames.rateUnit as keyof AddPrescriptionCardFormValues,
      RateUnit.Day,
    );
    setValue(
      fieldNames.howLongForValue as keyof AddPrescriptionCardFormValues,
      null,
    );
    setValue(
      fieldNames.howLongForUnit as keyof AddPrescriptionCardFormValues,
      TimeUnit.Day,
    );
  };

  const amountToDispense = useMemo(
    () =>
      calculateDispensableQuantity({
        howItsTaken: howItsTaken as AdministrationApproach,
        doseValue: Number(doseValue),
        doseUnit: doseUnit as DoseUnit,
        rateValue: Number(rateValue),
        rateUnit: rateUnit as RateUnit,
        howLongForValue: Number(howLongForValue),
        howLongForUnit: howLongForUnit as TimeUnit,
      }),
    [
      howItsTaken,
      doseValue,
      doseUnit,
      rateValue,
      rateUnit,
      howLongForValue,
      howLongForUnit,
    ],
  );

  // Resolve clinical product ID to VMP ID for nudge checking
  // If it's a VMP, use it directly. If it's an AMP, resolve to its parent VMP ID.
  const { result: vmpResult } = useRxData<Vmp>(
    RXDB_VMP_COLLECTION_NAME,
    (collection) =>
      clinicalProductId &&
      collection.find({ selector: { id: String(clinicalProductId) } }),
  );

  const { result: ampResult } = useRxData<Amp>(
    RXDB_AMP_COLLECTION_NAME,
    (collection) =>
      clinicalProductId &&
      collection.find({ selector: { id: String(clinicalProductId) } }),
  );

  // Determine the VMP ID for nudge checking
  const vmpIdForNudgeCheck = useMemo(() => {
    if (vmpResult.length > 0) {
      // It's a VMP, use its ID directly
      return vmpResult[0].id;
    }
    if (ampResult.length > 0) {
      // It's an AMP, use its parent VMP ID
      return ampResult[0].vmpId;
    }
    // Not found in either collection, return the original ID as fallback
    return clinicalProductId;
  }, [vmpResult, ampResult, clinicalProductId]);

  const currentDeviation = useMemo(() => {
    return checkMedicationGuidanceDeviation(vmpIdForNudgeCheck as string);
  }, [checkMedicationGuidanceDeviation, vmpIdForNudgeCheck]);

  const hasTrackedRef = useRef<DrugGuidanceDeviation | null>(null);

  useEffect(() => {
    if (
      currentDeviation !== DrugGuidanceDeviation.NONE &&
      hasTrackedRef.current !== currentDeviation &&
      trackRecommendationShown
    ) {
      trackRecommendationShown(currentDeviation);
      hasTrackedRef.current = currentDeviation;
    }
  }, [currentDeviation, trackRecommendationShown]);

  const GuidanceDeviationNudge: React.FC | null = () => {
    switch (currentDeviation) {
      case DrugGuidanceDeviation.NONE:
        return null;
      case DrugGuidanceDeviation.ANTIBIOTIC_NO_BACTERIAL_DIAGNOSIS: {
        return (
          <NudgeBoxContainer>
            <AntibioticPrescriptionNudgeInfoBox
              onAddDiagnosis={onAddDiagnosis}
            />
          </NudgeBoxContainer>
        );
      }
      case DrugGuidanceDeviation.ACT_PRESCRIPTION_NEGATIVE_MALARIA_TEST: {
        return (
          <NudgeBoxContainer>
            <MalarialPrescriptionNudgeWarningBox
              message={
                MalarialNudgeMessages.NEGATIVE_DIAGNOSTIC_RESULT_WITH_ANTI_MALARIAL
              }
            />
          </NudgeBoxContainer>
        );
      }
      case DrugGuidanceDeviation.NO_ACT_PRESCRIPTION_POSITIVE_MALARIA_TEST: {
        return (
          <NudgeBoxContainer>
            <MalarialPrescriptionNudgeWarningBox
              message={
                MalarialNudgeMessages.POSITIVE_DIAGNOSTIC_RESULT_WITHOUT_ANTI_MALARIAL
              }
            />
          </NudgeBoxContainer>
        );
      }
    }

    return null;
  };

  return (
    <Card color={CardColors.PRESCRIPTION}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <CardTitle>
          <FormattedMessage
            id="inpatient.prescribe.card.title"
            defaultMessage="Prescription"
          />
        </CardTitle>
        <IconButton
          onClick={onDelete}
          style={{ marginTop: -8, marginRight: -8 }}
          aria-label="Delete prescription"
        >
          <BinIcon />
        </IconButton>
      </div>
      <Typography variant="body1" style={{ marginBottom: 4 }}>
        <FormattedMessage
          id="add-prescription.patient.weight"
          defaultMessage="Weight: {weight}"
          values={{
            weight: (
              <strong>
                {patientWeight ? `${patientWeight} kg` : "No weight recorded"}
              </strong>
            ),
          }}
        />
      </Typography>
      <Typography variant="body1" style={{ marginBottom: 8 }}>
        <FormattedMessage
          id="add-prescription.patient.allergies"
          defaultMessage="Allergies: {allergies}"
          values={{
            allergies: (
              <strong>
                {patientAllergies.length > 0
                  ? patientAllergies.join(", ")
                  : "No allergies recorded"}
              </strong>
            ),
          }}
        />
      </Typography>
      <ClinicalProductAutocomplete
        mode={ClinicalProductAutocompleteMode.OUTPATIENT}
        clinicalProductIdName={fieldNames.clinicalProductId}
        clinicalProductTypeName={fieldNames.clinicalProductType}
        freetextName={fieldNames.freeTextProduct}
        clinicalProductChangeSideEffects={clinicalProductChangeSideEffects}
      />
      {!!GuidanceDeviationNudge && <GuidanceDeviationNudge />}
      <div style={{ marginTop: 16 }}>
        <RHFRadioField
          data-testid="prescription-dose-rate-quantity-unit"
          name={fieldNames.howItsTaken}
          label={formatMessage({
            id: "add-prescription.how-its-taken",
            defaultMessage: "How it's taken",
          })}
          required
          options={ADMINISTRATION_APPROACH_OPTIONS}
          padding="normal"
        />
      </div>
      <Collapse
        in={!!howItsTaken && (!!clinicalProductId || !!freeTextProduct)}
      >
        <AdministrationDetailInputs
          fieldNames={{
            howItsTaken: fieldNames.howItsTaken,
            doseValue: fieldNames.doseValue,
            doseUnit: fieldNames.doseUnit,
            rateValue: fieldNames.rateValue,
            rateUnit: fieldNames.rateUnit,
            howLongForValue: fieldNames.howLongForValue,
            howLongForUnit: fieldNames.howLongForUnit,
            clinicalProductId: fieldNames.clinicalProductId,
          }}
        />
        <Divider style={{ marginTop: 16 }} />
        <div>
          <Typography variant="body1" style={{ marginTop: 16 }}>
            <FormattedMessage
              id="add-prescription.note"
              defaultMessage="Notes"
            />
          </Typography>
          <RHFTextField
            name={fieldNames.note}
            label={formatMessage({
              id: "note.label",
              defaultMessage: "Additional note",
            })}
            minRows={4}
            multiline
            style={{ width: 340, maxWidth: "100%" }}
          />
        </div>
        {amountToDispense !== 0 && (
          <>
            <Divider style={{ marginTop: 16 }} />
            <div>
              <Typography
                variant="body1"
                style={{ marginTop: 16, marginBottom: 8 }}
              >
                <FormattedMessage
                  id="add-prescription.amount-to-dispense"
                  defaultMessage="Amount to dispense"
                />
              </Typography>
              <Typography
                variant="body1"
                style={{ marginTop: 16, marginBottom: 8 }}
              >
                <strong>{amountToDispense}</strong>
              </Typography>
            </div>
          </>
        )}
      </Collapse>
    </Card>
  );
};
